import { VolumeUp } from '@tunasong/icons'
import { Button, IconButton } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useId, useState } from 'react'
import { MelodyMiniRoll } from '../melody-roll/melody-mini-roll.js'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import type { PickerItem, PickerTag } from './picker-items.js'
import styles from './phrase-picker.module.css'

/**
 * The phrase picker (Pitch Trainer Phase 2, plan M5; Joni §2). A radio group: Arrow keys move between
 * rows, the selected row shows a filled indicator, not colour alone, and focus starts on the Suggested
 * row. Each row's `aria-hidden` thumbnail is described in words, with its difficulty.
 */

export interface PhrasePickerScope {
  sectionName: string
  value: 'section' | 'song'
  onChange: (value: 'section' | 'song') => void
}

export interface PhrasePickerProps {
  items: PickerItem[]
  isCalibrated: boolean
  /** Absent when opened from a melody block: the scope control is then hidden */
  scope?: PhrasePickerScope
  onHear: (item: PickerItem) => void
  onSing: (item: PickerItem) => void
  /** Pitfall 6: close and focus the source, the melody block or the chart */
  onEdit: (item: PickerItem) => void
  onMeasureRange: () => void
}

const levelWords = { easy: 'Easy', medium: 'Medium', hard: 'Hard' } as const

const Tag: FC<{ tag: PickerTag }> = ({ tag }) => {
  const copy = useVocalCopy(tag.lexiconId, tag.tokens)
  return copy ? <span className={styles.tag}>{copy.tag}</span> : null
}

interface RowProps {
  item: PickerItem
  name: string
  isChecked: boolean
  onSelect: () => void
  onHear: () => void
}

const Row: FC<RowProps> = ({ item, name, isChecked, onSelect, onHear }) => {
  const descriptionId = useId()
  return (
    <div className={isChecked ? `${styles.row} ${styles.checked}` : styles.row}>
      <label className={styles.choice}>
        <input
          type="radio"
          className={styles.radio}
          name={name}
          value={item.id}
          checked={isChecked}
          onChange={onSelect}
          aria-label={item.label}
          aria-describedby={descriptionId}
          /* Joni §2: focus lands on the Suggested row when the picker opens */
          autoFocus={item.isSuggested}
        />
        <span className={styles.text}>
          <span className={styles.label}>
            {item.label}
            {item.isSuggested ? <span className={styles.suggested}>Suggested</span> : null}
          </span>
          <span id={descriptionId} className={styles.details}>
            {item.kind === 'phrase' ? null : <span className={styles.subLabel}>{item.subLabel}</span>}
            {item.alsoIn ? <span>{`Also in ${item.alsoIn}`}</span> : null}
            <span className={styles.visuallyHidden}>{item.textAlternative}</span>
            {item.level ? <span className={styles.level}>{levelWords[item.level]}</span> : null}
            {item.tags.map(tag => (
              <Tag key={tag.lexiconId} tag={tag} />
            ))}
          </span>
        </span>
        <span className={styles.thumbnail} aria-hidden="true">
          <MelodyMiniRoll melody={item.thumbnail} interactive={false} showTempo={false} />
        </span>
      </label>
      <IconButton aria-label={item.hearName} onClick={onHear}>
        <VolumeUp fontSize="small" />
      </IconButton>
    </div>
  )
}

export const PhrasePicker: FC<PhrasePickerProps> = props => {
  const { items, isCalibrated, scope, onHear, onSing, onEdit, onMeasureRange } = props
  const name = useId()
  const [selectedId, setSelectedId] = useState(() => (items.find(item => item.isSuggested) ?? items[0])?.id)
  const selected = items.find(item => item.id === selectedId)
  const isChordOnly = items.length > 0 && items.every(item => item.kind !== 'phrase')
  const sections = items.reduce<string[]>(
    (names, item) => (names.includes(item.section) ? names : [...names, item.section]),
    []
  )

  return (
    <div className={styles.root}>
      {scope ? (
        <div role="group" aria-label="Scope" className={styles.scope}>
          {(['section', 'song'] as const).map(value => (
            <Button
              key={value}
              size="small"
              variant={scope.value === value ? 'contained' : 'outlined'}
              aria-pressed={scope.value === value}
              onClick={() => scope.onChange(value)}
            >
              {value === 'section' ? scope.sectionName : 'Whole song'}
            </Button>
          ))}
        </div>
      ) : null}
      {isCalibrated ? null : (
        <p className={styles.uncalibrated}>
          {/* Chord lines are not written anywhere: without a range they are built around G3–G4 (Joni, Bowie, #2515) */}
          {isChordOnly ? 'Sing in whichever octave feels comfortable — it still counts.' : 'Sung as written.'}{' '}
          <button type="button" className={styles.link} onClick={onMeasureRange}>
            Measure your range
          </button>{' '}
          {isChordOnly ? 'to fit these parts to your voice.' : 'to fit phrases to your voice.'}
        </p>
      )}
      <div role="radiogroup" aria-label={isChordOnly ? 'Parts' : 'Phrases'} className={styles.list}>
        {sections.map(section => (
          <section key={section} className={styles.section}>
            <h3 className={styles.heading}>{section}</h3>
            {items.some(item => item.section === section && item.kind !== 'phrase' && item.isOutlineOnly) ? (
              <p className={styles.sectionNote}>
                Guide tones would stay on one note here, so this section has chord outlines only.
              </p>
            ) : null}
            {items
              .filter(item => item.section === section)
              .map(item => (
                <Row
                  key={item.id}
                  item={item}
                  name={name}
                  isChecked={item.id === selectedId}
                  onSelect={() => setSelectedId(item.id)}
                  onHear={() => onHear(item)}
                />
              ))}
          </section>
        ))}
      </div>
      <div className={styles.footer}>
        {selected ? (
          <Button variant="text" onClick={() => onEdit(selected)}>
            {selected.kind === 'phrase' ? 'Edit melody' : 'Edit chords'}
          </Button>
        ) : null}
        <Button variant="contained" disabled={!selected} onClick={() => selected && onSing(selected)}>
          Sing it
        </Button>
      </div>
    </div>
  )
}
